/* Custom-size ordering on catalog rug pages.
 *
 * The estimate shown here mirrors the backend's ladder pricing exactly:
 * the price is interpolated between the two LISTED sizes bracketing the
 * area, so it always lands between its neighbours' prices, never shrinks
 * as the rug grows, and an exact listed size costs exactly its listed
 * price. The swatch (12"x12") anchors the ladder at 1 sqft — the 1'x1'
 * floor prices like the swatch. The charged price is always recomputed
 * server-side from the same ladder — this script only displays; the
 * add-to-cart request carries dimensions, never a price.
 */
(function () {
  'use strict';

  // One side of a size option, in any form the catalog actually uses:
  //   8'      8 ft            6'6"   6.5 ft (mixed feet+inches)
  //   12"     1 ft (swatch)   14     bare number = feet (missing foot mark)
  // Parsing per-dimension rather than matching the whole string keeps mixed
  // titles like "4' x 6'6"" and "2'6" x 10'" in the ladder. Dropping them
  // silently removed real anchors, which made a custom size at exactly that
  // listed size quote — and charge — LESS than the listed variant.
  var DIM_RE = /^\s*(\d+(?:\.\d+)?)\s*(?:(["”″])|['′’]\s*(?:(\d+(?:\.\d+)?)\s*["”″]?)?)?\s*$/;

  function dimToFeet(token) {
    var m = DIM_RE.exec(token);
    if (!m) return null;
    var n = parseFloat(m[1]);
    if (isNaN(n)) return null;
    if (m[2]) return n / 12;                       // pure inches
    return n + (m[3] ? parseFloat(m[3]) / 12 : 0); // feet, optional inches
  }

  function ladderDims(title) {
    var parts = String(title).replace(/^\s*swatch\s+/i, '').split(/[x×]/i);
    if (parts.length !== 2) return null;
    var w = dimToFeet(parts[0]);
    var l = dimToFeet(parts[1]);
    if (w === null || l === null || !(w > 0) || !(l > 0)) return null;
    return [w, l];
  }

  function ladderSqft(title) {
    var d = ladderDims(title);
    return d ? d[0] * d[1] : null;
  }

  /* The listed variant whose DIMENSIONS match exactly, if there is one.
   * Area alone cannot price every product: 3'x5' and 2.5'x6' are both 15 sqft
   * but listed at different prices, and averaging them quoted LESS than the
   * listed 3'x5'. An exact dimension match always wins. Mirrors
   * pricing.exact_listed_price. */
  function exactListedPrice(variants, widthFt, lengthFt) {
    var want = [Math.round(widthFt * 100) / 100, Math.round(lengthFt * 100) / 100].sort(function (a, b) { return a - b; });
    for (var i = 0; i < (variants || []).length; i++) {
      var v = variants[i];
      var d = ladderDims(String(v.title || ''));
      if (!d) continue;
      var have = [Math.round(d[0] * 100) / 100, Math.round(d[1] * 100) / 100].sort(function (a, b) { return a - b; });
      if (have[0] !== want[0] || have[1] !== want[1]) continue;
      var price = (v.price || 0) / 100;
      if (!(price > 0)) continue;
      var compare = (v.compare_at_price || 0) / 100;
      return { price: roundToX99(price), compareAt: compare > price ? roundToX99(compare) : 0 };
    }
    return null;
  }

  function roundToX99(price) {
    price = Math.round(price * 1e6) / 1e6;
    var floored = Math.floor(price);
    var candidate = floored + 0.99;
    if (candidate < price) candidate = floored + 1.99;
    return Math.round(candidate * 100) / 100;
  }

  function ladderFromVariants(variants) {
    var byArea = {};
    (variants || []).forEach(function (v) {
      var sqft = ladderSqft(String(v.title || ''));
      var price = (v.price || 0) / 100;
      if (!(sqft > 0) || !(price > 0)) return;
      var compare = (v.compare_at_price || 0) / 100;
      (byArea[sqft] = byArea[sqft] || []).push({ price: price, compare: compare > price ? compare : 0 });
    });
    return Object.keys(byArea).map(function (key) {
      var rows = byArea[key];
      var compares = rows.filter(function (r) { return r.compare; });
      return {
        sqft: parseFloat(key),
        price: rows.reduce(function (s, r) { return s + r.price; }, 0) / rows.length,
        compare: compares.length
          ? compares.reduce(function (s, r) { return s + r.compare; }, 0) / compares.length
          : 0
      };
    }).sort(function (a, b) { return a.sqft - b.sqft; });
  }

  // Linear PRICE interpolation between the two listed sizes bracketing the
  // area: always between the neighbours' prices, never lower for a bigger
  // rug, exact at listed sizes. Outside the ladder the boundary size's
  // per-sqft rate extends the line. Mirrors pricing._bracket_price.
  function bracketPrice(anchors, sqft) {
    var first = anchors[0];
    var last = anchors[anchors.length - 1];
    if (sqft <= first.sqft) return (first.value / first.sqft) * sqft;
    if (sqft >= last.sqft) return (last.value / last.sqft) * sqft;
    for (var i = 0; i < anchors.length - 1; i++) {
      var lo = anchors[i], hi = anchors[i + 1];
      if (sqft >= lo.sqft && sqft <= hi.sqft) {
        return lo.value + (hi.value - lo.value) * (sqft - lo.sqft) / (hi.sqft - lo.sqft);
      }
    }
    return (last.value / last.sqft) * sqft;
  }

  function priceFor(ladder, sqft) {
    var raw = bracketPrice(ladder.map(function (p) { return { sqft: p.sqft, value: p.price }; }), sqft);
    var price = roundToX99(raw);
    var compareAnchors = ladder.filter(function (p) { return p.compare; })
      .map(function (p) { return { sqft: p.sqft, value: p.compare }; });
    var compareAt = 0;
    if (compareAnchors.length >= 2) {
      compareAt = roundToX99(bracketPrice(compareAnchors, sqft));
      if (compareAt <= price) compareAt = 0;
    }
    return { price: price, compareAt: compareAt, rate: raw / sqft };
  }

  /* Eurus's own cart-add contract — the same one assets/product-cart.js uses.
   * The shopper stays on the page and the drawer opens over it with the
   * merchant's upsells. Deliberately NOT the cart permalink: a permalink
   * navigates to the .myshopify.com domain AND replaces the cart, throwing
   * away whatever the shopper had already added. */
  function addVariantToThemeCart(variantId, qty) {
    var sections = window.Alpine.store('xCartHelper').getSectionsToRender();
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(qty || 1));
    formData.append('sections', sections.map(function (s) { return s.id; }));
    formData.append('sections_url', window.location.pathname);

    return fetch(window.Eurus.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (response) {
      if (String(response.status) === '422') {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }
      sections.forEach(function (section) {
        var el = document.querySelector(section.selector);
        if (el && response.sections && response.sections[section.id]) {
          el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
        }
      });
      if (window.Alpine.store('xMiniCart')) window.Alpine.store('xMiniCart').openCart();
      var bubble = document.querySelector('#cart-icon-bubble span');
      if (bubble) window.Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      return response;
    });
  }

  function themeCartAvailable() {
    return !!(window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper') &&
      window.Eurus && window.Eurus.cart_add_url && typeof getSectionInnerHTML === 'function');
  }

  /* Fallback when the drawer isn't available: add through the standard cart
   * API and then show the cart. Never the /cart/<variant>:<qty> permalink —
   * that both leaves the storefront domain for *.myshopify.com and REPLACES
   * the cart, silently discarding anything the shopper had already added. */
  function addThenGoToCart(variantId) {
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (r) { return r.json(); }).then(function (body) {
      if (body && body.status && String(body.status) !== '200') {
        throw new Error(body.description || body.message || 'Add to cart failed');
      }
      window.location.href = '/cart';
    });
  }

  /* ---------- "Custom size" option inside the Size picker ----------
   * The shopper is already choosing a size in that row, so the entry point
   * belongs there rather than as a separate link underneath. The button clones
   * the classes off a real size option so it matches the theme exactly (and
   * keeps matching if the merchant restyles the picker). The theme re-renders
   * the picker on every variant change, which destroys the injected node, so
   * a MutationObserver puts it back.
   */
  function sizeOptionLabels() {
    var radios = Array.prototype.slice.call(document.querySelectorAll('input[type="radio"]'))
      .filter(function (r) { return /^size(-\d+)?\s*$/i.test(r.name || ''); });
    return radios.map(function (r) {
      return document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(r.id) : r.id) + '"]');
    }).filter(function (l) { return l && l.offsetParent !== null; });
  }

  function injectSizeButton(root, config, onToggle) {
    var labels = sizeOptionLabels();
    if (!labels.length) return false;
    var container = labels[0].parentElement;
    if (!container || container.querySelector('[data-cs-size-btn]')) return true;

    // CLONE a real option rather than building a <button>. Same element type,
    // same classes, same inner markup — so it cannot drift from the theme.
    // A <button> is not interchangeable here: this theme styles buttons as
    // 100px pills with appearance:button and centred text, so a button-based
    // option renders as a pill among square size chips.
    // Clone an UNSELECTED option, so the resting appearance is the resting one
    // (labels[0] may be the currently checked size).
    var ref = labels.filter(function (l) {
      var input = l.htmlFor ? document.getElementById(l.htmlFor) : null;
      return input && !input.checked;
    })[0] || labels[0];
    var opt = ref.cloneNode(true);
    opt.removeAttribute('for');
    opt.removeAttribute('id');
    // A clone can carry the source option's own controls/markup; keep only a
    // single span so the label text is all that differs.
    var refSpan = ref.querySelector('span');
    opt.innerHTML = '';
    var span = document.createElement('span');
    if (refSpan && refSpan.className) span.className = refSpan.className;
    span.textContent = config.sizeButtonLabel || 'Custom size';
    opt.appendChild(span);

    opt.classList.add('cs-rug__size-btn');
    opt.setAttribute('data-cs-size-btn', '');
    // role=button is needed for assistive tech, but this theme also STYLES
    // [role="button"] as a 100px pill with light text, which would make this
    // option a pill among square size chips. Pin the two affected properties
    // to whatever a real option computes to, so it tracks the theme rather
    // than hardcoding a look. The selected state overrides these with
    // !important in the stylesheet.
    var refStyle = getComputedStyle(ref);
    opt.style.borderRadius = refStyle.borderRadius;
    opt.style.color = refStyle.color;
    opt.setAttribute('role', 'button');
    opt.setAttribute('tabindex', '0');
    opt.setAttribute('aria-expanded', String(!root.querySelector('[data-cs-panel]').hidden));
    var panelId = root.querySelector('[data-cs-panel]').id;
    if (panelId) opt.setAttribute('aria-controls', panelId);

    // A re-render drops the class with the old fieldset; restore it so an open
    // panel keeps the standard option suppressed.
    container.classList.toggle('cs-rug-custom-active', !root.querySelector('[data-cs-panel]').hidden);

    opt.addEventListener('click', onToggle);
    opt.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onToggle();
      }
    });
    container.appendChild(opt);
    return true;
  }

  /* While the custom panel is open the shopper's size IS the custom one, so the
   * standard option must not keep looking selected too. The radio is left
   * checked (the theme's variant state depends on it) — only its selected
   * styling is neutralised, using values read from a real unselected sibling so
   * this stays correct whatever palette the merchant sets. */
  function readJson(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function initWidget(root) {
    var config = readJson(root, '[data-cs-config]') || {};
    var rawVariants = readJson(root, '[data-cs-ladder]');
    var ladder = ladderFromVariants(rawVariants);
    if (ladder.length < 2) return; // no size curve to price from — stay hidden

    var el = {
      panel: root.querySelector('[data-cs-panel]'),
      shape: root.querySelector('[data-cs-shape]'),
      shapeRow: root.querySelector('[data-cs-shape-row]'),
      widthLabel: root.querySelector('[data-cs-width-label]'),
      widthFt: root.querySelector('[data-cs-width-ft]'),
      widthIn: root.querySelector('[data-cs-width-in]'),
      lengthRow: root.querySelector('[data-cs-length-row]'),
      lengthFt: root.querySelector('[data-cs-length-ft]'),
      lengthIn: root.querySelector('[data-cs-length-in]'),
      price: root.querySelector('[data-cs-price]'),
      compare: root.querySelector('[data-cs-compare]'),
      add: root.querySelector('[data-cs-add]'),
      error: root.querySelector('[data-cs-error]')
    };
    if (!el.panel || !el.widthFt || !el.add) return;

    root.hidden = false;
    if (el.shapeRow && el.shape && el.shape.options.length < 2) el.shapeRow.hidden = true;

    function shape() { return el.shape ? el.shape.value : 'rectangular'; }
    function singleDimension() { return shape() === 'round' || shape() === 'square'; }

    function dims() {
      var width = parseInt(el.widthFt.value, 10) + parseInt(el.widthIn.value, 10) / 12;
      var length = singleDimension()
        ? width
        : parseInt(el.lengthFt.value, 10) + parseInt(el.lengthIn.value, 10) / 12;
      return { width: width, length: length };
    }

    function sqft() {
      var d = dims();
      return shape() === 'round' ? Math.PI * Math.pow(d.width / 2, 2) : d.width * d.length;
    }

    function render() {
      if (el.lengthRow) el.lengthRow.hidden = singleDimension();
      if (el.widthLabel) {
        el.widthLabel.textContent = shape() === 'round' ? config.labelDiameter
          : shape() === 'square' ? config.labelSide : config.labelWidth;
      }
      // An exact dimension match wins over the area ladder — a custom size
      // that IS a listed size must show that listed price. Round is excluded:
      // its area is a circle, so it never equals a listed W x L variant.
      var d = dims();
      var exact = shape() === 'round' ? null : exactListedPrice(rawVariants, d.width, d.length);
      var quote = exact || priceFor(ladder, sqft());
      el.price.textContent = formatMoney(quote.price, config.currency);
      if (el.compare) {
        el.compare.textContent = quote.compareAt ? formatMoney(quote.compareAt, config.currency) : '';
        el.compare.hidden = !quote.compareAt;
      }
      el.error.hidden = true;
    }

    function fail(message) {
      el.error.textContent = message || config.errorText;
      el.error.hidden = false;
      el.add.disabled = false;
      el.add.textContent = config.buttonLabel;
    }

    function endpoint() {
      var path = 'custom-size/checkout/';
      if (config.proxyPath) return config.proxyPath.replace(/\/$/, '') + '/' + path;
      return (config.apiBaseUrl || '').replace(/\/$/, '') + '/api/ruggen/' + path;
    }

    function track(name, data) {
      if (window.dataLayer && window.dataLayer.push) {
        window.dataLayer.push(Object.assign({ event: 'custom_size_' + name }, data || {}));
      }
    }

    function setPanel(open) {
      if (el.panel.hidden === !open) return;   // already in that state
      el.panel.hidden = !open;
      root.classList.toggle('cs-rug--open', open);
      var btn = document.querySelector('[data-cs-size-btn]');
      if (btn) {
        btn.setAttribute('aria-expanded', String(open));
        // Suppresses the standard option's selected styling — see the
        // .cs-rug-custom-active rule in custom-size-rug.css.
        if (btn.parentElement) btn.parentElement.classList.toggle('cs-rug-custom-active', open);
      }
      if (open) {
        render();
        track('opened');
        el.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    function togglePanel() { setPanel(el.panel.hidden); }

    /* Custom size and a standard size are alternatives, so choosing a standard
     * one closes the custom panel and drops its selected styling. Capture phase
     * because the theme stops propagation on some pickers. */
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name || !/^size(-\d+)?\s*$/i.test(t.name)) return;
      if (el.panel.hidden) return;
      setPanel(false);
    }, true);

    injectSizeButton(root, config, togglePanel);
    // The theme swaps the variant picker out on every size change, taking the
    // injected option with it; put it back whenever that happens.
    var reinject = null;
    var picker = new MutationObserver(function () {
      if (reinject) return;
      reinject = setTimeout(function () {
        reinject = null;
        injectSizeButton(root, config, togglePanel);
      }, 0);
    });
    picker.observe(document.body, { childList: true, subtree: true });

    [el.shape, el.widthFt, el.widthIn, el.lengthFt, el.lengthIn].forEach(function (input) {
      input && input.addEventListener('change', render);
    });

    el.add.addEventListener('click', function () {
      var d = dims();
      el.add.disabled = true;
      el.add.textContent = config.buttonBusyLabel;
      el.error.hidden = true;
      track('add_to_cart', { shape: shape(), width: d.width, length: d.length });

      fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: config.productId,
          shape: shape(),
          width_ft: parseInt(el.widthFt.value, 10),
          width_in: parseInt(el.widthIn.value, 10),
          length_ft: singleDimension() ? 0 : parseInt(el.lengthFt.value, 10),
          length_in: singleDimension() ? 0 : parseInt(el.lengthIn.value, 10),
          quantity: 1
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok) throw new Error(body.error || Object.values(body || {})[0] || config.errorText);
          return body;
        });
      }).then(function (res) {
        // A MOCK_VARIANT URL means the backend has no store credentials —
        // never send a live shopper to that dead cart link.
        if (res.checkout_url && res.checkout_url.indexOf('MOCK_VARIANT') > -1) {
          throw new Error(config.errorText);
        }
        var finish = function () {
          el.add.disabled = false;
          el.add.textContent = config.buttonAddedLabel;
          setTimeout(function () { el.add.textContent = config.buttonLabel; }, 2500);
          track('added', { variant_id: res.variant_id });
        };
        if (!res.variant_id) throw new Error(config.errorText);
        if (themeCartAvailable()) {
          return addVariantToThemeCart(res.variant_id, 1).then(finish).catch(function () {
            return addThenGoToCart(res.variant_id);
          });
        }
        return addThenGoToCart(res.variant_id);
      }).catch(function (err) {
        fail(err && err.message);
      });
    });

    render();
  }

  function boot() {
    document.querySelectorAll('[data-custom-size-rug]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
